import {
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import * as PDFJS from 'pdfjs-dist';
import { getDocument, GlobalWorkerOptions, VerbosityLevel } from 'pdfjs-dist';
import {
  assign,
  createEventBus,
  isSSR,
  PDFDocumentLoadingTask,
  PDFDocumentProxy,
  PDFPageProxy,
  PDFProgressData,
  PDFSource,
  PDFViewerOptions,
  RenderTextMode,
  TextLayerRendered,
  ZoomScale,
} from '@core/pdf-viewer/data-access';
import * as PDFJSViewer from 'pdfjs-dist/web/pdf_viewer.mjs';
import { debounceTime, filter, from, fromEvent, Subject, takeUntil } from 'rxjs';

if (!isSSR()) {
  assign(PDFJS, 'verbosity', VerbosityLevel.INFOS);
}

// @ts-expect-error This does not exist outside of polyfill which this is doing
if (typeof Promise.withResolvers === 'undefined' && window) {
  // @ts-expect-error This does not exist outside of polyfill which this is doing
  window.Promise.withResolvers = () => {
    let resolve;
    let reject;
    const promise = new Promise((res, rej) => {
      resolve = res;
      reject = rej;
    });
    return { promise, resolve, reject };
  };
}

/**
 * Copy from {@link https://www.npmjs.com/package/ng2-pdf-viewer ng2-pdf-viewer} to make extensions easier.
 *
 * {@link https://github.com/VadimDez/ng2-pdf-viewer/blob/master/src/app/pdf-viewer/pdf-viewer.component.ts Source file}
 */
@Component({
  selector: 'core-pdf-viewer',
  standalone: true,
  imports: [],
  templateUrl: './pdf-viewer.component.html',
  styleUrls: ['./pdf-viewer.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class PdfViewerComponent implements OnChanges, OnInit, OnDestroy, AfterViewChecked {
  static CSS_UNITS = 96.0 / 72.0;
  static BORDER_WIDTH = 9;
  private isVisible = false;
  private _imageResourcesPath =
    typeof PDFJS !== 'undefined' ? `https://unpkg.com/pdfjs-dist@${(PDFJS as any).version}/web/images/` : undefined;
  private _pdf: PDFDocumentProxy | undefined;
  private _canAutoResize = true;
  private lastLoaded!: string | Uint8Array | PDFSource | null;
  private _latestScrolledPage!: number;
  private _textLayerRendered = new Set<TextLayerRendered>();
  private pageScrollTimeout: number | null = null;
  private isInitialized = false;
  private loadingTask?: PDFDocumentLoadingTask | null;
  private destroy$ = new Subject<void>();
  private originalDPR = window.devicePixelRatio || 1;
  eventBus!: PDFJSViewer.EventBus;
  pdfLinkService!: PDFJSViewer.PDFLinkService;
  pdfFindController!: PDFJSViewer.PDFFindController;
  pdfViewer!: PDFJSViewer.PDFViewer | PDFJSViewer.PDFSinglePageViewer;
  @ViewChild('pdfViewerContainer') pdfViewerContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('printAreaRef', { static: true }) printAreaRef!: ElementRef<HTMLIFrameElement>;
  @ViewChild('pdfViewerRef', { static: true }) pdfViewerRef!: ElementRef<HTMLDivElement>;
  @Output() afterLoadComplete = new EventEmitter<PDFDocumentProxy>();
  @Output() pageRendered = new EventEmitter<CustomEvent>();
  @Output() pageInitialized = new EventEmitter<CustomEvent>();
  @Output() textLayerRendered = new EventEmitter<TextLayerRendered>();
  @Output() allTextLayersRendered = new EventEmitter<Set<TextLayerRendered>>();
  @Output() errorOccurred = new EventEmitter<any>();
  @Output() progressUpdate = new EventEmitter<PDFProgressData>();
  @Output() pageChange: EventEmitter<number> = new EventEmitter<number>();
  @Output() scaleChange: EventEmitter<number> = new EventEmitter<number>();
  @Input() src?: string | Uint8Array | PDFSource;

  constructor(private element: ElementRef<HTMLElement>, private ngZone: NgZone) {
    if (isSSR()) {
      return;
    }

    let pdfWorkerSrc: string;

    const pdfJsVersion: string = (PDFJS as any).version;
    const versionSpecificPdfWorkerUrl: string = (window as any)[`pdfWorkerSrc${pdfJsVersion}`];

    if (versionSpecificPdfWorkerUrl) {
      pdfWorkerSrc = versionSpecificPdfWorkerUrl;
    } else if (
      Object.hasOwnProperty.call(window, 'pdfWorkerSrc') &&
      typeof (window as any).pdfWorkerSrc === 'string' &&
      (window as any).pdfWorkerSrc
    ) {
      pdfWorkerSrc = (window as any).pdfWorkerSrc;
    } else {
      pdfWorkerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfJsVersion}/legacy/build/pdf.worker.min.mjs`;
    }

    assign(GlobalWorkerOptions, 'workerSrc', pdfWorkerSrc);
  }

  private _cMapsUrl =
    typeof PDFJS !== 'undefined' ? `https://unpkg.com/pdfjs-dist@${(PDFJS as any).version}/cmaps/` : null;

  @Input() set cMapsUrl(cMapsUrl: string) {
    this._cMapsUrl = cMapsUrl;
  }

  private _renderText = true;

  @Input() set renderText(renderText: boolean) {
    this._renderText = renderText;
  }

  private _renderTextMode: RenderTextMode = RenderTextMode.ENABLED;

  @Input() set renderTextMode(renderTextMode: RenderTextMode) {
    this._renderTextMode = renderTextMode;
  }

  private _stickToPage = false;

  @Input() set stickToPage(value: boolean) {
    this._stickToPage = value;
  }

  private _originalSize = true;

  @Input() set originalSize(originalSize: boolean) {
    this._originalSize = originalSize;
  }

  private _page = 1;

  @Input() set page(_page: number | string | any) {
    _page = parseInt(_page, 10) || 1;
    const originalPage = _page;

    if (this._pdf) {
      _page = this.getValidPageNumber(_page);
    }

    this._page = _page;
    if (originalPage !== _page) {
      this.pageChange.emit(_page);
    }
  }

  private _zoom = 1;

  get zoom() {
    return this._zoom;
  }

  @Input() set zoom(value: number) {
    if (value <= 0) {
      return;
    }

    this._zoom = value;
  }

  private _zoomScale: ZoomScale = 'page-width';

  get zoomScale() {
    return this._zoomScale;
  }

  @Input() set zoomScale(value: ZoomScale) {
    this._zoomScale = value;
  }

  private _rotation = 0;

  @Input() set rotation(value: unknown) {
    if (!(typeof value === 'number' && value % 90 === 0)) {
      console.warn('Invalid pages rotation angle.');
      return;
    }

    this._rotation = value;
  }

  private _showAll = true;

  @Input() set showAll(value: boolean) {
    this._showAll = value;
  }

  private _fitToPage = false;

  @Input() set fitToPage(value: boolean) {
    this._fitToPage = Boolean(value);
  }

  private _externalLinkTarget = 'blank';

  @Input() set externalLinkTarget(value: string) {
    this._externalLinkTarget = value;
  }

  private _showBorders = false;

  @Input() set showBorders(value: boolean) {
    this._showBorders = Boolean(value);
  }

  @Input() set autoresize(value: boolean) {
    this._canAutoResize = Boolean(value);
  }

  static getLinkTarget(type: string) {
    switch (type) {
      case 'blank':
        return PDFJSViewer.LinkTarget.BLANK;
      case 'none':
        return PDFJSViewer.LinkTarget.NONE;
      case 'self':
        return PDFJSViewer.LinkTarget.SELF;
      case 'parent':
        return PDFJSViewer.LinkTarget.PARENT;
      case 'top':
        return PDFJSViewer.LinkTarget.TOP;
    }

    return null;
  }

  ngAfterViewChecked(): void {
    if (this.isInitialized) {
      return;
    }

    const offset = this.pdfViewerContainer.nativeElement.offsetParent;

    if (this.isVisible && offset == null) {
      this.isVisible = false;
      return;
    }

    if (!this.isVisible && offset != null) {
      this.isVisible = true;

      setTimeout(() => {
        this.initialize();
        this.ngOnChanges({ src: this.src } as any);
      });
    }
  }

  ngOnInit(): void {
    this.initialize();
    this.setupResizeListener();
    this.overrideDevicePixelRatio();
  }

  ngOnDestroy(): void {
    this.clear();
    this.destroy$.next();
    this.loadingTask = null;
    this.restoreDevicePixelRatio();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (isSSR() || !this.isVisible) {
      return;
    }

    if ('src' in changes) {
      this.loadPDF();
    } else if (this._pdf) {
      if ('renderText' in changes || 'showAll' in changes) {
        this.setupViewer();
        this.resetPdfDocument();
      }
      if ('page' in changes) {
        const { page } = changes;
        if (page.currentValue === this._latestScrolledPage) {
          return;
        }

        // New form of page changing: The viewer will now jump to the specified page when it is changed.
        // This behavior is introduced by using the PDFSinglePageViewer
        this.pdfViewer.scrollPageIntoView({ pageNumber: this._page });
      }

      this.update();
    }
  }

  public updateSize(): void {
    from(this._pdf!.getPage(this.pdfViewer.currentPageNumber))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (page: PDFPageProxy) => {
          const rotation = this._rotation + page.rotate;
          const viewportWidth =
            page.getViewport({
              scale: this._zoom,
              rotation,
            }).width * PdfViewerComponent.CSS_UNITS;
          let scale = this._zoom;
          let stickToPage = true;

          // Scale the document when it shouldn't be in original size or doesn't fit into the viewport
          if (
            !this._originalSize ||
            (this._fitToPage && viewportWidth > this.pdfViewerContainer.nativeElement.clientWidth)
          ) {
            const viewPort = page.getViewport({ scale: 1, rotation });
            scale = this.getScale(viewPort.width, viewPort.height);
            stickToPage = !this._stickToPage;
          }

          this.pdfViewer.currentScale = scale;
          this.scaleChange.emit(scale);
          if (stickToPage) {
            this.pdfViewer.scrollPageIntoView({ pageNumber: page.pageNumber, ignoreDestinationZoom: true });
          }

          this.setSizeForComponent();
        },
      });
  }

  public clear() {
    if (this.loadingTask && !this.loadingTask.destroyed) {
      this.loadingTask.destroy();
    }

    if (this._pdf) {
      this._latestScrolledPage = 0;
      this._pdf.destroy().then(() => (this._pdf = undefined));
    }

    this.pdfViewer && this.pdfViewer.setDocument(null as any);
    this.pdfLinkService && this.pdfLinkService.setDocument(null, null);
    this.pdfFindController && this.pdfFindController.setDocument(null as any);
  }

  private overrideDevicePixelRatio(): void {
    this.storeOriginalDPR();
    this.setDevicePixelRatio(1); // Set to 1 or any desired value
    console.log('Overridden devicePixelRatio:', window.devicePixelRatio);
  }

  private storeOriginalDPR(): void {
    this.originalDPR = window.devicePixelRatio || 1;
  }

  private setDevicePixelRatio(value: number): void {
    Object.defineProperty(window, 'devicePixelRatio', {
      value: value,
      writable: false, // Prevent further modifications
    });
  }

  private restoreDevicePixelRatio(): void {
    if (this.originalDPR !== undefined) {
      this.setDevicePixelRatio(this.originalDPR);
      console.log('Restored devicePixelRatio:', window.devicePixelRatio);
    }
  }

  private getPDFLinkServiceConfig() {
    const linkTarget = PdfViewerComponent.getLinkTarget(this._externalLinkTarget);

    if (linkTarget) {
      return { externalLinkTarget: linkTarget };
    }

    return {};
  }

  private initEventBus() {
    this.eventBus = createEventBus(PDFJSViewer, this.destroy$);

    fromEvent<CustomEvent>(this.eventBus, 'pagerendered')
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        this.pageRendered.emit(event);
      });

    fromEvent<CustomEvent>(this.eventBus, 'pagesinit')
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        this.pageInitialized.emit(event);
      });

    fromEvent(this.eventBus, 'pagechanging')
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ pageNumber }: any) => {
        if (this.pageScrollTimeout) {
          clearTimeout(this.pageScrollTimeout);
        }

        this.pageScrollTimeout = window.setTimeout(() => {
          this._latestScrolledPage = pageNumber;
          this.pageChange.emit(pageNumber);
        }, 100);
      });

    fromEvent<TextLayerRendered>(this.eventBus, 'textlayerrendered')
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        this.textLayerRendered.emit(event);
        this._textLayerRendered.add(event);
        if (event.pageNumber === this._pdf?.numPages) {
          this.allTextLayersRendered.emit(this._textLayerRendered);
        }
      });
  }

  private initPDFServices() {
    this.pdfLinkService = new PDFJSViewer.PDFLinkService({
      eventBus: this.eventBus,
      ...this.getPDFLinkServiceConfig(),
    });
    this.pdfFindController = new PDFJSViewer.PDFFindController({
      eventBus: this.eventBus,
      linkService: this.pdfLinkService,
    });
  }

  private getPDFOptions(): PDFViewerOptions {
    return {
      eventBus: this.eventBus,
      container: this.element.nativeElement.querySelector('div')!,
      removePageBorders: !this._showBorders,
      linkService: this.pdfLinkService,
      textLayerMode: this._renderText ? this._renderTextMode : RenderTextMode.DISABLED,
      findController: this.pdfFindController,
      l10n: new PDFJSViewer.GenericL10n('en'),
      imageResourcesPath: this._imageResourcesPath,
      annotationEditorMode: PDFJS.AnnotationEditorType.DISABLE,
    };
  }

  private setupViewer() {
    if (this.pdfViewer) {
      this.pdfViewer.setDocument(null as any);
    }

    assign(PDFJS, 'disableTextLayer', !this._renderText);

    this.initPDFServices();

    if (this._showAll) {
      this.pdfViewer = new PDFJSViewer.PDFViewer(this.getPDFOptions());
    } else {
      this.pdfViewer = new PDFJSViewer.PDFSinglePageViewer(this.getPDFOptions());
    }
    this.pdfLinkService.setViewer(this.pdfViewer);

    this.pdfViewer._currentPageNumber = this._page;
  }

  private getValidPageNumber(page: number): number {
    if (page < 1) {
      return 1;
    }

    if (page > this._pdf!.numPages) {
      return this._pdf!.numPages;
    }

    return page;
  }

  private getDocumentParams() {
    const srcType = typeof this.src;

    if (!this._cMapsUrl) {
      return this.src;
    }

    const params: any = {
      cMapUrl: this._cMapsUrl,
      cMapPacked: true,
      enableXfa: true,
    };
    params.isEvalSupported = false; // http://cve.org/CVERecord?id=CVE-2024-4367

    if (srcType === 'string') {
      params.url = this.src;
    } else if (srcType === 'object') {
      if ((this.src as any).byteLength !== undefined) {
        params.data = this.src;
      } else {
        Object.assign(params, this.src);
      }
    }

    return params;
  }

  private loadPDF() {
    if (!this.src) {
      return;
    }

    if (this.lastLoaded === this.src) {
      this.update();
      return;
    }

    this.clear();

    this.setupViewer();

    this.loadingTask = getDocument(this.getDocumentParams());

    this.loadingTask!.onProgress = (progressData: PDFProgressData) => {
      this.progressUpdate.emit(progressData);
    };

    const src = this.src;

    from(this.loadingTask!.promise as Promise<PDFDocumentProxy>)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: pdf => {
          this._pdf = pdf;
          this.lastLoaded = src;

          this.afterLoadComplete.emit(pdf);
          this.resetPdfDocument();

          this.update();
        },
        error: error => {
          this.lastLoaded = null;
          this.errorOccurred.emit(error);
        },
      });
  }

  private update() {
    this.page = this._page;

    this.render();
  }

  private render() {
    this._page = this.getValidPageNumber(this._page);

    if (this._rotation !== 0 || this.pdfViewer.pagesRotation !== this._rotation) {
      // wait until at least the first page is available.
      this.pdfViewer.firstPagePromise?.then(() => (this.pdfViewer.pagesRotation = this._rotation));
    }

    if (this._stickToPage) {
      setTimeout(() => {
        this.pdfViewer.currentPageNumber = this._page;
      });
    }

    if (!this.pdfViewer._pages?.length) {
      // the first time we wait until pages init
      const sub = this.pageInitialized.subscribe(() => {
        this.updateSize();
        sub.unsubscribe();
      });
    } else {
      this.updateSize();
    }
  }

  private getScale(viewportWidth: number, viewportHeight: number) {
    const borderSize = this._showBorders ? 2 * PdfViewerComponent.BORDER_WIDTH : 0;
    const pdfContainerWidth = this.pdfViewerContainer.nativeElement.clientWidth - borderSize;
    const pdfContainerHeight = this.pdfViewerContainer.nativeElement.clientHeight - borderSize;

    if (pdfContainerHeight === 0 || viewportHeight === 0 || pdfContainerWidth === 0 || viewportWidth === 0) {
      return 1;
    }

    let ratio = 1;
    switch (this._zoomScale) {
      case 'page-fit':
        ratio = Math.min(pdfContainerHeight / viewportHeight, pdfContainerWidth / viewportWidth);
        break;
      case 'page-height':
        ratio = pdfContainerHeight / viewportHeight;
        break;
      case 'page-width':
      default:
        ratio = pdfContainerWidth / viewportWidth;
        break;
    }

    return (this._zoom * ratio) / PdfViewerComponent.CSS_UNITS;
  }

  private setSizeForComponent(): void {
    this.element.nativeElement.style.width = this.pdfViewerContainer.nativeElement.clientWidth + 'px';
    this.element.nativeElement.style.height = this.pdfViewerContainer.nativeElement.clientHeight + 'px';
  }

  private resetPdfDocument() {
    this.pdfLinkService.setDocument(this._pdf, null);
    this.pdfFindController.setDocument(this._pdf!);
    this.pdfViewer.setDocument(this._pdf!);
  }

  private initialize(): void {
    if (isSSR() || !this.isVisible) {
      return;
    }

    this.isInitialized = true;
    this.initEventBus();
    this.setupViewer();
  }

  private setupResizeListener(): void {
    if (isSSR()) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      fromEvent(window, 'resize')
        .pipe(
          debounceTime(100),
          filter(() => this._canAutoResize && !!this._pdf),
          takeUntil(this.destroy$)
        )
        .subscribe(() => this.updateSize());
    });
  }
}
